import {
  Checkbox,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { featureDescription, featureRequirements } from "../../../constants";
import { getValidFeatures } from "../utils";
import { makeStyles } from "@material-ui/core/styles";
import config from "../../../../../config";

const useStyles = makeStyles((theme) => ({
  featureCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: theme.shadows[3],
    },
    cursor: "pointer",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  featureCardDisabled: {
    opacity: 0.7,
    backgroundColor: theme.palette.action.disabledBackground,
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
  },
  checkbox: {
    padding: theme.spacing(0.5),
    marginLeft: -theme.spacing(0.5),
  },
  labelContent: {
    marginTop: 4,
  },
  compactCheckbox: {
    padding: theme.spacing(0.25),
  },
  compactListItemIcon: {
    minWidth: 32,
  },
  compactFeatureName: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  compactFeatureDesc: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
  compactDisabled: {
    opacity: 0.5,
  },
}));

const checkRequirements = (feature, features, partner) => {
  const requirements = featureRequirements[feature];

  const needsPlaid =
    config.appEnv === "production" &&
    feature === "boom_screen" &&
    !partner?.features?.includes("boom_screen") &&
    !partner?.plaidClientId;

  if (needsPlaid) return { matches: false, tooltip: "Requires ONE OF: Enable Plaid for BoomScreen" };
  if (!requirements) return { matches: true, tooltip: "" };
  if ("or" in requirements) {
    const matches = requirements.or.some((req) => features.includes(req));
    return { matches, tooltip: matches ? "" : `Requires ONE OF: ${requirements.or.join(", ")}` };
  }
  if ("and" in requirements) {
    const missing = requirements.and.filter((req) => !features.includes(req));
    return { matches: missing.length === 0, tooltip: missing.length ? `Missing "AND" requirements: ${missing.join(", ")}` : "" };
  }
  return { matches: false, tooltip: "" };
};

const makeHandleChange = (feature, features, setFeatures) => (checked) => {
  const initial = checked
    ? [...features, feature]
    : features.filter((f) => f !== feature);
  setFeatures(getValidFeatures(initial));
};

const wrapWithTooltip = (element, tooltip) => {
  if (!tooltip) return element;
  return (
    <Tooltip title={tooltip}>
      <div>{element}</div>
    </Tooltip>
  );
};

const CompactFeatureCheckbox = ({ feature, features, setFeatures, partner, indent }) => {
  const classes = useStyles();
  const { matches, tooltip } = checkRequirements(feature, features, partner);
  const isChecked = features.includes(feature);
  const handleChange = makeHandleChange(feature, features, setFeatures);

  const listItem = (
    <ListItem
      dense
      button
      disabled={!matches}
      className={!matches ? classes.compactDisabled : undefined}
      style={indent ? { paddingLeft: 40 * Math.min(indent, 2) } : undefined}
      onClick={() => matches && handleChange(!isChecked)}
    >
      <ListItemIcon className={classes.compactListItemIcon}>
        <Checkbox
          edge="start"
          checked={isChecked}
          disabled={!matches}
          className={classes.compactCheckbox}
          size="small"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleChange(e.target.checked)}
        />
      </ListItemIcon>
      <ListItemText
        primary={<div className={classes.compactFeatureName}>{feature}</div>}
        secondary={
          <div className={classes.compactFeatureDesc}>
            {featureDescription[feature]}
          </div>
        }
        disableTypography
      />
    </ListItem>
  );

  return wrapWithTooltip(listItem, tooltip);
};

const CardFeatureCheckbox = ({ feature, features, setFeatures, partner, level }) => {
  const classes = useStyles();
  const { matches, tooltip } = checkRequirements(feature, features, partner);
  const isChecked = features.includes(feature);
  const handleChange = makeHandleChange(feature, features, setFeatures);

  const checkbox = (
    <Paper
      className={`${classes.featureCard} ${!matches ? classes.featureCardDisabled : ""}`}
      style={{ marginLeft: `${level * 24}px` }}
      elevation={isChecked ? 2 : 1}
      onClick={() => matches && handleChange(!isChecked)}
    >
      <div className={classes.checkboxWrapper}>
        <Checkbox
          checked={isChecked}
          disabled={!matches}
          onChange={(e) => handleChange(e.target.checked)}
          className={classes.checkbox}
          onClick={(e) => e.stopPropagation()}
        />
        <div className={classes.labelContent}>
          <Typography variant="body1" component="h3">
            {feature}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {featureDescription[feature]}
          </Typography>
        </div>
      </div>
    </Paper>
  );

  return wrapWithTooltip(checkbox, tooltip);
};

export const FeatureCheckbox = ({
  feature,
  features,
  setFeatures,
  partner,
  level = 0,
  compact = false,
  indent = 0,
}) => {
  if (compact) {
    return (
      <CompactFeatureCheckbox
        feature={feature}
        features={features}
        setFeatures={setFeatures}
        partner={partner}
        indent={indent}
      />
    );
  }
  return (
    <CardFeatureCheckbox
      feature={feature}
      features={features}
      setFeatures={setFeatures}
      partner={partner}
      level={level}
    />
  );
};
